import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  ChevronLeft,
  Bell,
  Mail,
  MessageSquare,
  Smartphone,
  Globe,
  DollarSign,
  Loader2,
  Save,
  Printer,
  Bluetooth,
  Usb,
  Wifi,
  RefreshCw,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';
import PushNotificationManager from '@/components/PushNotificationManager';

import { API } from '@/config/api';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' }
];

const printerTypes = [
  { value: 'none', label: 'None', icon: '❌' },
  { value: 'bluetooth', label: 'Bluetooth', icon: '📶' },
  { value: 'usb', label: 'USB', icon: '🔌' },
  { value: 'wifi', label: 'Wi-Fi/Network', icon: '📡' }
];

const paperWidths = [
  { value: '58mm', label: '58mm (2.25")' },
  { value: '80mm', label: '80mm (3.15")' }
];

export default function SettingsPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token } = useAuthStore();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchSettings = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
    setHasChanges(true);
    
    // Immediately change language when selected
    if (key === 'language') {
      i18n.changeLanguage(value);
      toast.success(t('settings.languageChanged', { fallback: `Language changed to ${languages.find(l => l.code === value)?.name || value}` }));
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="settings-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold">Settings</h1>
          </div>
          {hasChanges && (
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Changes
            </Button>
          )}
        </div>

        {loading ? (
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-1/3 mb-4" />
                <div className="space-y-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex justify-between">
                      <Skeleton className="h-5 w-1/2" />
                      <Skeleton className="h-6 w-10" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Notification Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Notifications
                </CardTitle>
                <CardDescription>
                  Manage how you receive updates
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label>Push Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Get notified about orders and promotions
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.push_notifications}
                    onCheckedChange={(v) => updateSetting('push_notifications', v)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label>Email Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive order confirmations via email
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.email_notifications}
                    onCheckedChange={(v) => updateSetting('email_notifications', v)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label>SMS Notifications</Label>
                      <p className="text-sm text-muted-foreground">
                        Get text messages for delivery updates
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.sms_notifications}
                    onCheckedChange={(v) => updateSetting('sms_notifications', v)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <Label>Order Updates</Label>
                      <p className="text-sm text-muted-foreground">
                        Real-time updates on your orders
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={settings?.order_updates}
                    onCheckedChange={(v) => updateSetting('order_updates', v)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Push Notifications Manager */}
            <PushNotificationManager />

            {/* Marketing Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Marketing
                </CardTitle>
                <CardDescription>
                  Choose what promotional content you receive
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Promotional Emails</Label>
                    <p className="text-sm text-muted-foreground">
                      Deals, discounts, and special offers
                    </p>
                  </div>
                  <Switch
                    checked={settings?.promotional_emails}
                    onCheckedChange={(v) => updateSetting('promotional_emails', v)}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Newsletter</Label>
                    <p className="text-sm text-muted-foreground">
                      Weekly updates and new restaurants
                    </p>
                  </div>
                  <Switch
                    checked={settings?.newsletter}
                    onCheckedChange={(v) => updateSetting('newsletter', v)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Preferences */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Preferences
                </CardTitle>
                <CardDescription>
                  Customize your experience
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <Label>{t('settings.language')}</Label>
                  </div>
                  <Select
                    value={settings?.language || i18n.language}
                    onValueChange={(v) => updateSetting('language', v)}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <span className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.name}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <Label>Currency</Label>
                  </div>
                  <Select
                    value={settings?.currency}
                    onValueChange={(v) => updateSetting('currency', v)}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (\u20ac)</SelectItem>
                      <SelectItem value="GBP">GBP (\u00a3)</SelectItem>
                      <SelectItem value="CAD">CAD (C$)</SelectItem>
                      <SelectItem value="AUD">AUD (A$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Printer Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Printer className="h-5 w-5" />
                  {t('settings.printer')}
                </CardTitle>
                <CardDescription>
                  Configure receipt printer for POS
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Label>{t('settings.printerType')}</Label>
                  </div>
                  <Select
                    value={settings?.printer_type || 'none'}
                    onValueChange={(v) => updateSetting('printer_type', v)}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {printerTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <span>{type.icon}</span>
                            <span>{type.label}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {settings?.printer_type && settings.printer_type !== 'none' && (
                  <>
                    <Separator />
                    
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>{t('settings.printerName')}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={settings?.printer_name || ''}
                            onChange={(e) => updateSetting('printer_name', e.target.value)}
                            placeholder="Enter printer name"
                            className="w-40"
                          />
                          <Badge variant={settings?.printer_connected ? "default" : "secondary"}>
                            {settings?.printer_connected ? (
                              <><CheckCircle className="h-3 w-3 mr-1" /> {t('settings.connected')}</>
                            ) : (
                              <><XCircle className="h-3 w-3 mr-1" /> {t('settings.disconnected')}</>
                            )}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <Label>{t('settings.paperWidth')}</Label>
                        <Select
                          value={settings?.paper_width || '80mm'}
                          onValueChange={(v) => updateSetting('paper_width', v)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {paperWidths.map((w) => (
                              <SelectItem key={w.value} value={w.value}>
                                {w.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <Separator />

                      <div className="flex items-center justify-between">
                        <div>
                          <Label>{t('settings.autoPrint')}</Label>
                          <p className="text-sm text-muted-foreground">
                            Automatically print receipt after order
                          </p>
                        </div>
                        <Switch
                          checked={settings?.auto_print}
                          onCheckedChange={(v) => updateSetting('auto_print', v)}
                        />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            updateSetting('scanning_printers', true);
                            toast.info('Scanning for printers...');
                            setTimeout(() => {
                              updateSetting('scanning_printers', false);
                              toast.success('Scan complete');
                            }, 2000);
                          }}
                          disabled={settings?.scanning_printers}
                        >
                          {settings?.scanning_printers ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                          )}
                          {t('settings.scanDevices')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            toast.info('Sending test print...');
                            setTimeout(() => {
                              toast.success('Test print sent!');
                            }, 1000);
                          }}
                        >
                          <Printer className="h-4 w-4 mr-2" />
                          {t('settings.testPrint')}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Save Button (mobile) */}
            {hasChanges && (
              <Button onClick={saveSettings} className="w-full" disabled={saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Changes
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
